<template>
  <div class="card card-block d-flex h-100">
    <div v-if="showCardHeader" class="card-header bg-primary">
      {{ title }}
    </div>
    <div
      class="card-body align-items-center d-flex justify-content-center"
      style="flex-direction: column"
    >
      <template v-if="hasVideo">
        <div class="video-container">
          <video
            :src="video"
            height="944"
            width="676"
            style="max-height: clamp(200px, 400px, 33vh); max-width: 60vw"
            :style="'aspect-ratio: ' + imageRatio"
            controls
          ></video>
        </div>
      </template>
      <template v-else>
        <div class="mt-5 mb-5 text-muted">
          <div
            style="
              display: flex;
              place-items: center;
              place-content: center;
              text-align: center;
              max-height: clamp(200px, 400px, 33vh);
              background-color: var(--gray-light);
              width: 300px;
              max-width: 60vw;
            "
            :style="'aspect-ratio: ' + imageRatio"
          >
            <h3>
              {{
                $t('components/video-upload.no_video.' + attributeGender, {
                  attribute: attributeName
                })
              }}
            </h3>
          </div>
        </div>
      </template>
    </div>
    <div class="card-footer bg-transparent text-center">
      <!-- Modal Trigger Button -->
      <button
        v-if="!hasVideo"
        type="button"
        class="btn btn-secondary"
        @click="showUploadModal = true"
      >
        {{
          $t('components/video-upload.buttons.choose_video.' + attributeGender, {
            attribute: attributeName
          })
        }}
      </button>
      <template v-else>
        <button type="button" class="btn btn-danger mr-1" @click="showDeleteModal = true">
          {{ $t('components/video-upload.buttons.delete') }}
        </button>
        <button type="button" class="btn btn-secondary" @click="showUploadModal = true">
          {{ $t('components/video-upload.buttons.update_video') }}
        </button>
      </template>
    </div>
  </div>
  <!-- Upload Modal -->
  <ModalComponent :visible="showUploadModal" ref="uploadModal" @close="showUploadModal = false">
    <template #title>
      <div class="modal-title">
        {{ title }}
      </div>
    </template>
    <template #body>
      <div class="container">
        <div class="row">
          <div class="col-12 notification-section" v-if="hasErrors">
            <div class="alert alert-danger">
              <ul class="mb-0">
                <li v-for="error in form.errors">{{ error }}</li>
              </ul>
            </div>
          </div>
          <div class="col-12">
            <div class="alert alert-info">
              <span v-if="imageRatio"
                >{{
                  $t('components/video-upload.size-info', {
                    aspect_ratio: imageRatio,
                    max_size: maxFileSize,
                    type: acceptedFileTypes
                  })
                }}
              </span>
            </div>
            <!-- File Input -->
            <div class="form-group">
              <label for="videoFile">
                {{ $t('components/video-upload.buttons.choose_file') }}
              </label>
              <input
                type="file"
                class="form-control-file"
                id="videoFile"
                @change="handleFileChange"
                accept="video/mp4"
              />
            </div>

            <!-- Progress Bar -->
            <div class="progress mt-3" v-if="form.progress">
              <div
                class="progress-bar"
                role="progressbar"
                :style="{ width: form.progress.percentage + '%' }"
                :aria-valuenow="form.progress.percentage"
                aria-valuemin="0"
                aria-valuemax="100"
              >
                {{ form.progress.percentage }}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <button
        type="button"
        class="btn btn-secondary"
        :disabled="form.processing && !hasErrors"
        @click="showUploadModal = false"
      >
        {{ $t('crud.close') }}
      </button>
      <button
        type="button"
        class="btn btn-primary"
        @click="uploadVideo"
        :disabled="!form.video || form.processing"
      >
        {{ $t('components/video-upload.buttons.save') }}
      </button>
    </template>
  </ModalComponent>
  <!-- Delete Modal -->
  <ModalComponent :visible="showDeleteModal" ref="deleteModal" @close="showDeleteModal = false">
    <template #title>
      <h5 class="modal-title">
        {{ deleteTitle }}
      </h5>
    </template>
    <template #body>
      <div class="modal-body">
        {{
          $t('components/video-upload.delete.message-gender.' + attributeGender, {
            attribute: attributeName
          })
        }}
      </div>
    </template>
    <template #footer>
      <button type="button" class="btn btn-secondary" @click="showDeleteModal = false">
        {{ $t('crud.close') }}
      </button>
      <button type="button" class="btn btn-danger" @click="deleteVideo">
        {{ $t('components/video-upload.buttons.delete') }}
      </button>
    </template>
  </ModalComponent>
</template>

<script>
import { useForm } from '@inertiajs/vue3'
import { wTransChoice } from 'laravel-vue-i18n'
import ModalComponent from '@components/modals/ModalComponent.vue'
import { ref } from 'vue'
export default {
  name: 'VideoUploadModal',
  components: {
    ModalComponent
  },
  props: {
    uploadRoute: {
      type: String,
      required: true
    },
    deleteRoute: {
      type: String,
      required: true
    },
    showCardHeader: {
      type: Boolean,
      default: true
    },
    video: {
      type: String,
      default: ''
    },
    textContent: {
      type: Object,
      default: () => ({
        title: 'Upload Video',
        no_video: 'No Video',
        buttons: {
          choose_video: 'Choose Video',
          update_video: 'Update Video',
          delete: 'Delete'
        }
      })
    },
    attributeName: {
      type: String,
      default: 'video'
    },
    attributeGender: {
      type: String,
      default: 'female'
    },
    shape: {
      type: String,
      default: 'portrait'
    },
    acceptedFileTypes: {
      type: String,
      default: ''
    },
    maxFileSize: {
      type: String,
      default: '8 MB'
    },
    customTitle: {
      type: String,
      default: null
    }
  },
  setup() {
    const showDeleteModal = ref(false)
    const showUploadModal = ref(false)
    return {
      showDeleteModal,
      showUploadModal
    }
  },
  data() {
    return {
      imageRatio: '676/944',
      deleteForm: useForm({}),
      form: useForm({
        video: null
      })
    }
  },
  computed: {
    hasErrors() {
      return Object.keys(this.form.errors).length > 0
    },
    title() {
      if (this.customTitle) {
        return this.customTitle
      }
      return wTransChoice('components/video-upload.title', 1, {
        attribute: this.attributeName,
        shape: this.$t('components/video-upload.shapes.' + this.shape)
      })
    },
    deleteTitle() {
      return this.$t('components/video-upload.delete.title-gender.' + this.attributeGender, {
        attribute: this.attributeName
      })
    },
    hasVideo() {
      return this.video && this.video.length > 0
    }
  },
  methods: {
    handleFileChange(event) {
      this.form.video = event.target.files[0]
    },
    getUpdateRoute() {
      return this.updateRoute
    },
    getDeleteRoute() {
      return this.deleteRoute
    },
    async uploadVideo() {
      if (!this.form.video) {
        return
      }

      await this.form.post(this.uploadRoute, {
        onProgress: progress => {
          this.$refs.uploadModal.$el.style.cursor = 'wait'
        },
        onSuccess: () => {
          this.showUploadModal = false
          this.$refs.uploadModal.$el.style.cursor = 'default'
        },
        onError: error => {
          console.error(error)
          this.$refs.uploadModal.$el.style.cursor = 'default'
        },
        preserveState: true
      })
    },
    async deleteVideo() {
      await this.deleteForm.delete(this.deleteRoute, {
        onProgress: progress => {
          this.$refs.deleteModal.$el.style.cursor = 'wait'
        },
        onSuccess: () => {
          this.$refs.deleteModal.$el.style.cursor = 'default'
          this.showDeleteModal = false
        },
        onError: error => {
          console.error(error)
          this.$refs.deleteModal.$el.style.cursor = 'default'
        },
        preserveState: true
      })
    }
  }
}
</script>
